import { useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { PROVISIONING, SETTINGS } from "../../components/nav";

/**
 * A page for an area the owner wants in the sidebar before its backend exists
 * (2026-09-14: "provisioning is missing", "Devices and Settings should be
 * also…"). It says honestly what the entry will hold, rather than rendering
 * an empty table that reads as a broken screen.
 */
function NotBuilt({ section, title, will }: { section: string; title: string; will: string }) {
  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="overline" color="text.secondary">
        {section}
      </Typography>
      <Typography variant="h5" component="h1" gutterBottom>
        {title}
      </Typography>
      <Alert severity="info">
        <AlertTitle>Not available yet</AlertTitle>
        {will}
      </Alert>
    </Box>
  );
}

/**
 * Provisioning › NAS / Switches / Servers / Routers.
 *
 * ⛔ NOT BUILT. No service in the estate holds an inventory of these
 * resources yet (measured 2026-09-14: the closest thing is the NAS list
 * FreeRADIUS reads, which has no HTTP route).
 */
export function ProvisioningScreen() {
  const { resource } = useParams();
  const item = PROVISIONING.find((p) => p.slug === resource);
  if (!item) return null;
  const noun = item.label === "NAS" ? "NAS" : item.label.toLowerCase();
  return (
    <NotBuilt
      section="Provisioning"
      title={item.label}
      will={`This page will list the ${noun} AnkhOS manages for your namespace — name, IP address, status and current bandwidth — and let you add or retire one. Nothing in the platform records that inventory yet, so there is nothing to show here today.`}
    />
  );
}

const SETTINGS_WILL: Record<(typeof SETTINGS)[number]["slug"], string> = {
  preferences:
    "This page will hold your operator settings: the company logo shown in the console and on your portals, portal preferences, and console preferences such as the colour mode default. Nothing in the platform stores an operator preference yet, so there is nothing to set here today. The colour-mode toggle in the header works now and is remembered by this browser.",
  bng: "This page will hold the settings of your broadband network gateways (BNG): the RADIUS shared secret, NAS identifier and accounting interval each gateway uses with AnkhOS, and which gateway serves which site. Nothing in the platform records a gateway's settings yet, so there is nothing to set here today.",
  tr069:
    "This page will hold your TR-069 (CWMP) settings: the ACS URL and credentials your customer devices are provisioned with, and the firmware and configuration profiles pushed to them. AnkhOS has no ACS connection yet, so there is nothing to set here today.",
};

/**
 * Settings › Preferences / BNG / TR069: the operator's own settings areas
 * (the brief's extensible preferences area, plus the two the owner added on
 * 2026-09-15). ⛔ NOT BUILT: no backend stores any of these yet.
 */
export function SettingsScreen() {
  const { item } = useParams();
  const entry = SETTINGS.find((x) => x.slug === item);
  if (!entry) return null;
  return <NotBuilt section="Settings" title={entry.label} will={SETTINGS_WILL[entry.slug]} />;
}
